'use client';

import { useState } from 'react';

interface Subsection {
  id: string;
  title: string;
  content: string;
}

interface TutorialSection {
  id: string;
  title: string;
  icon: string;
  subsections: Subsection[];
}

// Tutorial sections data
const tutorialSections: TutorialSection[] = [
  {
    id: 'audience',
    title: 'Participants Management',
    icon: '👥',
    subsections: [
      {
        id: 'audience-basics',
        title: 'Participants Basics',
        content: `
          <h4 class="text-xl font-semibold mb-4">Understanding Your Audience</h4>
          <p class="mb-4">Effective campaigns start with your participants</p>
          <div class="rounded h-60 flex items-center justify-center mb-4">
            <p>Screenshot placeholder: Audience dashboard</p>
          </div>
          <p class="mb-4">Spark allows you to segment your audience based on various attributes:</p>
          <ul class="list-disc ml-6 space-y-2 mb-4">
            <li>Demographics (age, location, job title)</li>
            <li>Behavior patterns (previous interactions)</li>
            <li>Custom attributes specific to your organization</li>
            <li>Engagement level with past campaigns</li>
          </ul>
        `
      },
      {
        id: 'creating-segments',
        title: 'Creating Segments',
        content: `
          <h4 class="text-xl font-semibold mb-4">Creating Audience Segments</h4>
          <p class="mb-4">Follow these steps to create targeted audience segments:</p>
          <div class="space-y-6 my-4">
            <div>
              <h5 class="font-medium text-lg">Step 1: Access Audience Manager</h5>
              <p class="mb-2">Navigate to the "Audience" section in the main menu.</p>
              <div class="rounded h-40 flex items-center justify-center">
                <p>Screenshot placeholder: Audience section navigation</p>
              </div>
            </div>
            
            <div>
              <h5 class="font-medium text-lg">Step 2: Create New Segment</h5>
              <p class="mb-2">Click "Create Segment" to start defining your audience criteria.</p>
              <div class="rounded h-40 flex items-center justify-center">
                <p>Screenshot placeholder: Create segment button</p>
              </div>
            </div>
            
            <div>
              <h5 class="font-medium text-lg">Step 3: Define Segment Rules</h5>
              <p class="mb-2">Use the rule builder to set conditions for your segment.</p>
              <div class="rounded h-40 flex items-center justify-center">
                <p>Screenshot placeholder: Segment rule builder</p>
              </div>
            </div>
          </div>
        `
      }
    ]
  },
  {
    id: 'content',
    title: 'Content Creation',
    icon: '✏️',
    subsections: [
      {
        id: 'content-editor',
        title: 'Using the Content Editor',
        content: `
          <h4 class="text-xl font-semibold mb-4">Content Editor Basics</h4>
          <p class="mb-4">The Spark content editor provides powerful tools to create engaging campaign content.</p>
          <div class="rounded h-60 flex items-center justify-center mb-4">
            <p>Screenshot placeholder: Content editor interface</p>
          </div>
          <p class="mb-4">Key features of the content editor include:</p>
          <ul class="list-disc ml-6 space-y-2 mb-4">
            <li>Drag-and-drop elements</li>
            <li>Rich text formatting</li>
            <li>Image and video embedding</li>
            <li>Personalization tokens</li>
            <li>Mobile preview and responsive design tools</li>
          </ul>
        `
      },
      {
        id: 'personalization',
        title: 'Content Personalization',
        content: `
          <h4 class="text-xl font-semibold mb-4">Personalizing Your Content</h4>
          <p class="mb-4">Personalization can significantly improve engagement rates for your campaigns.</p>
          <div class="rounded h-60 flex items-center justify-center mb-4">
            <p>Screenshot placeholder: Personalization tokens menu</p>
          </div>
          <p class="mb-4">Spark offers several personalization options:</p>
          <div class="space-y-4">
            <div class="border-l-4 pl-4">
              <h5 class="font-medium text-lg">Basic Tokens</h5>
              <p>Insert recipient name, company, title, etc. using simple tokens like {firstName}.</p>
            </div>
            
            <div class="border-l-4 pl-4">
              <h5 class="font-medium text-lg">Conditional Content</h5>
              <p>Display different content blocks based on recipient attributes.</p>
            </div>
            
            <div class="border-l-4 pl-4">
              <h5 class="font-medium text-lg">Dynamic Images</h5>
              <p>Use different images based on segment, location, or other variables.</p>
            </div>
          </div>
        `
      }
    ]
  },
  {
    id: 'campaigns',
    title: 'Creating Campaigns',
    icon: '📣',
    subsections: [
      {
        id: 'campaign-basics',
        title: 'Campaign Basics',
        content: `
          <h4 class="text-xl font-semibold mb-4">Campaign Basics</h4>
          <p class="mb-4">Campaigns are the core of Spark's functionality, these give you the capability to send your audience forms to fill out.</p>
          <div class="rounded flex items-center justify-center mb-4">
            <img 
              src="/admin-dashboard.png" 
              alt="Campaign dashboard overview" 
              class="rounded mb-4 max-w-full h-auto" 
            />
          </div>
          <p className="mt-10">A campaign consists of several components:</p>
          <ul class="list-disc ml-6 space-y-2 my-4">
            <li>Participants are where you manage the people that will be receiving campaigns. This is your user database of people to choose from to receive a campaign./li>
            <li>Campaigns are where you can create and manage campaigns.</li>
            <li>Forms are where you create re-usable forms for campaigns. This is what the participants will fill out.</li>
            <li>Emails gives you the ability to customize how you notify your participants</li>
            <li>Reports lets you view the results of different forrms. </li>
          </ul>
          <p>Each component plays a crucial role in creating effective campaigns that drive engagement. Before you build your first campaign. Make sure you handle participants, emails, and forms.</p>
        `
      },
      {
        id: 'campaign-creation',
        title: 'Creating a New Campaign',
        content: `
          <h4 class="text-xl font-semibold mb-4">Creating a New Campaign</h4>
          <p class="mb-4">Follow these steps to create your first campaign:</p>
          <div class="space-y-6 my-4">
            <div>
              <h5 class="font-medium text-lg">Step 1: Navigate to Campaigns</h5>
              <p class="mb-2">Click on the "Campaigns" tab in the main navigation menu.</p>
            </div>
            
            <div>
              <h5 class="font-medium text-lg">Step 2: Click "Create Campaign"</h5>
              <p class="mb-2">Look for the "Create Campaign" button in the top Left corner.</p>
            </div>
            
            <div>
              <h5 class="font-medium text-lg">Step 3: Configure Campaign Settings</h5>
              <p class="mb-2">Fill out the campaign details form with your campaign name, description, what email template to use, who the participants will be, and a schedule for it. </p>
              <div class="rounded flex items-center justify-center">
                <img 
                  src="/create-campaign.png" 
                  alt="Campaign dashboard overview" 
                  class="rounded mb-4 max-w-full h-auto" 
                />
              </div>
            </div>
          </div>
        `
      },
      {
        id: 'campaign-templates',
        title: 'Using Campaign Templates',
        content: `
          <h4 class="text-xl font-semibold mb-4">Using Campaign Templates</h4>
          <p class="mb-4">Templates can save you time and ensure consistency across your campaigns.</p>
          <div class="rounded h-60 flex items-center justify-center mb-4">
            <p>Screenshot placeholder: Template gallery</p>
          </div>
          <p class="mb-4">Spark offers several pre-built templates for different campaign types:</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div class="border p-3 rounded">
              <h5 class="font-medium">Welcome Series</h5>
              <p class="text-sm">Perfect for onboarding new users</p>
            </div>
            <div class="border p-3 rounded">
              <h5 class="font-medium">Feedback Request</h5>
              <p class="text-sm">Gather insights from your audience</p>
            </div>
            <div class="border p-3 rounded">
              <h5 class="font-medium">Announcement</h5>
              <p class="text-sm">Share news and updates</p>
            </div>
            <div class="border p-3 rounded">
              <h5 class="font-medium">Custom Template</h5>
              <p class="text-sm">Create your own reusable template</p>
            </div>
          </div>
        `
      }
    ]
  },
  {
    id: 'analytics',
    title: 'Analytics & Reporting',
    icon: '📊',
    subsections: [
      {
        id: 'metrics-overview',
        title: 'Key Metrics Overview',
        content: `
          <h4 class="text-xl font-semibold mb-4">Understanding Campaign Metrics</h4>
          <p class="mb-4">Spark provides comprehensive analytics to measure campaign performance.</p>
          <div class="rounded h-60 flex items-center justify-center mb-4">
            <p>Screenshot placeholder: Analytics dashboard</p>
          </div>
          <p class="mb-4">Key metrics to monitor include:</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div class="border p-4 rounded">
              <h5 class="font-medium">Open Rate</h5>
              <p class="text-sm">Percentage of recipients who opened the campaign</p>
            </div>
            <div class="border p-4 rounded">
              <h5 class="font-medium">Click-Through Rate</h5>
              <p class="text-sm">Percentage of recipients who clicked on a link</p>
            </div>
            <div class="border p-4 rounded">
              <h5 class="font-medium">Conversion Rate</h5>
              <p class="text-sm">Percentage of recipients who completed a desired action</p>
            </div>
            <div class="border p-4 rounded">
              <h5 class="font-medium">Bounce Rate</h5>
              <p class="text-sm">Percentage of campaigns that couldn't be delivered</p>
            </div>
          </div>
        `
      },
      {
        id: 'reports',
        title: 'Creating Reports',
        content: `
          <h4 class="text-xl font-semibold mb-4">Building Custom Reports</h4>
          <p class="mb-4">Create tailored reports to share insights with your team and stakeholders.</p>
          <div class="rounded h-60 flex items-center justify-center mb-4">
            <p>Screenshot placeholder: Report builder interface</p>
          </div>
          <p class="mb-4">Follow these steps to create a custom report:</p>
          <div class="space-y-6 my-4">
            <div>
              <h5 class="font-medium text-lg">Step 1: Access Reports</h5>
              <p class="mb-2">Navigate to the "Reports" section in the Analytics menu.</p>
              <div class="rounded h-40 flex items-center justify-center">
                <p>Screenshot placeholder: Reports navigation</p>
              </div>
            </div>
            
            <div>
              <h5 class="font-medium text-lg">Step 2: Create New Report</h5>
              <p class="mb-2">Click "New Report" to begin building your custom report.</p>
              <div class="rounded h-40 flex items-center justify-center">
                <p>Screenshot placeholder: New report button</p>
              </div>
            </div>
            
            <div>
              <h5 class="font-medium text-lg">Step 3: Select Metrics</h5>
              <p class="mb-2">Choose which metrics to include in your report.</p>
              <div class="rounded h-40 flex items-center justify-center">
                <p>Screenshot placeholder: Metric selection interface</p>
              </div>
            </div>
          </div>
        `
      }
    ]
  }
];

export default function TutorialLayout() {
  const [activeSection, setActiveSection] = useState<string>(tutorialSections[0].id);
  const [activeSubsection, setActiveSubsection] = useState<string>(tutorialSections[0].subsections[0].id);
  
  // Find the currently active section
  const currentSection = tutorialSections.find(section => section.id === activeSection) || tutorialSections[0];
  
  // Find the currently active subsection
  const currentSubsection = currentSection.subsections.find(
    subsection => subsection.id === activeSubsection
  ) || currentSection.subsections[0];

  return (
    <div className="space-y-6">
      <p>Welcome to the Spark admin tutorials! Here you'll find comprehensive guides to help you manage your Spark platform.</p>
      
      {/* Main tutorial navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tutorialSections.map((section) => (
          <button
            key={section.id}
            className={`p-4 flex flex-col items-center rounded-lg transition border ${
              activeSection === section.id 
                ? 'border-2'
                : 'border hover:opacity-80'
            }`}
            onClick={() => {
              setActiveSection(section.id);
              setActiveSubsection(section.subsections[0].id);
            }}
          >
            <span className="text-2xl mb-2">{section.icon}</span>
            <span className="font-medium text-center">{section.title}</span>
          </button>
        ))}
      </div>
      
      {/* Subsection navigation and content */}
      <div className="rounded-lg shadow-md overflow-hidden">
        {/* Subsection tabs */}
        <div className="flex overflow-x-auto border-b scrollbar-hide">
          {currentSection.subsections.map((subsection) => (
            <button
              key={subsection.id}
              className={`px-4 py-3 whitespace-nowrap font-medium transition ${
                activeSubsection === subsection.id
                  ? 'border-b-2'
                  : 'hover:opacity-80'
              }`}
              onClick={() => setActiveSubsection(subsection.id)}
            >
              {subsection.title}
            </button>
          ))}
        </div>
        
        {/* Content area */}
        <div className="p-6">
          <div dangerouslySetInnerHTML={{ __html: currentSubsection.content }} />
        </div>
      </div>
      
      <div className="text-center">
        <p>Need more help? Contact our support team at <a href="mailto:support@viapeople.com" className="hover:underline">support@viapeople.com</a></p>
      </div>
    </div>
  );
}